import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref } from 'vue';
import { defineMessages } from '@/lib/i18n';
import { store } from '@/store/index';
import { RoleId } from '@/lib/enum';
import { readAssertedUserProfile } from '@/store/main/getters';
import {
  CreateCompanyPeriodicExportSettingRequest,
  GetCompanyPeriodicExportSetting,
  GetSendableCompanyUser,
  UpdateCompanyPeriodicExportSettingRequest,
} from '@/interfaces/api/general/company';
import PeriodicExportFormDialog, {
  ExportSettingForm,
} from '@/views/pages/companyPeriodicExport/PeriodicExportFormDialog.vue';
import PeriodicExportTable from '@/views/pages/companyPeriodicExport/PeriodicExportTable.vue';
import { EXPORT_FILE_FORMAT_EXCEL } from '@/lib/constant';
import { useSdsesExport } from '@/composables/useSdsesExport';
import { readAssertedCompanyId } from '@/store/main/getters';
import { AxiosError, AxiosResponse } from 'axios';
import {
  dispatchCreateCompanyPeriodicExportSetting,
  dispatchDeleteCompanyPeriodicExportSetting,
  dispatchGetCompanyPeriodicExportSettings,
  dispatchGetSendableCompanyUsers,
  dispatchUpdateCompanyPeriodicExportSetting,
} from '@/store/main/actionsMain';
import { commitAddNotification } from '@/store/main/mutations';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'PeriodicExportPage',
  setup(__props) {

const MAX_SETTINGS = 10; // 有効な定期エクスポート設定の上限数

const m = defineMessages({
  createLabel: '定期エクスポートを新規作成する',
  createSuccess: '定期エクスポート設定を追加しました',
  pageViewPermission: '管理者でないため、このページを表示できません',
  settingsLimitError:
    '現在ご契約のプランでは、定期エクスポート設定ができるのは{count}件までです。{count}件を超える設定が必要な場合は、ケミカンメニューバー右上の「お問い合わせ」から追加オプションの購入をリクエストしてください。',
});

const { exportOperations, fetchCompanyExportOperations } = useSdsesExport();
const isOpenCreateDialog = ref(false);
const hasInitializedError = ref(false);
const exportSettings = ref<GetCompanyPeriodicExportSetting[]>([]);
const sendableUsers = ref<GetSendableCompanyUser[]>([]);
const role = readAssertedUserProfile(store).role.id;
const companyId = readAssertedCompanyId(store);

const showCreateDialog = () => {
  isOpenCreateDialog.value = true;
};

const closeCreateDialog = () => {
  isOpenCreateDialog.value = false;
};

const isLimitSettings = (
  settings: GetCompanyPeriodicExportSetting[]
): boolean => {
  return settings.filter((v) => v.enabled).length >= MAX_SETTINGS;
};

const createExportSetting = async (form: ExportSettingForm) => {
  // リスク評価はファイル形式・エクスポート形式・文字コードの選択を持たないため送信しない
  const isRiskAssessment = form.exportType === 'risk_assessment';
  const body: CreateCompanyPeriodicExportSettingRequest = {
    name: form.name,
    description: form.description,
    export_type: form.exportType,
    export_file_format: isRiskAssessment ? undefined : form.exportFileFormat,
    export_format: isRiskAssessment ? undefined : form.exportFormat,
    export_character_code:
      !isRiskAssessment && form.exportFileFormat !== EXPORT_FILE_FORMAT_EXCEL
        ? form.exportCharacterCode
        : undefined,
    export_time: form.exportTime,
    export_frequency: form.exportFrequency,
    export_date_pattern:
      form.exportFrequency === 'monthly' ? form.exportDatePattern : undefined,
    export_day_of_week:
      form.exportFrequency === 'weekly' ? form.exportDayOfWeek : undefined,
    cc_user_ids: form.recipientCc,
  };

  try {
    const response: AxiosResponse =
      await dispatchCreateCompanyPeriodicExportSetting(store, {
        companyId,
        body,
      });
    if (response?.data) {
      commitAddNotification(store, {
        messageKey: m.createSuccess,
        color: 'success',
      });
      await fetchCompanyPeriodicExportSettings(companyId);
    }
  } catch (e) {
    if (e instanceof AxiosError) {
      const detail = e.response?.data.detail;
      commitAddNotification(store, {
        messageKey: sharedMessages.responseError,
        messageValues: {
          detail: detail ? detail : e.response?.data ?? e.message,
        },
        color: 'error',
      });
    } else {
      commitAddNotification(store, {
        messageKey: sharedMessages.genericError,
        color: 'error',
      });
    }
  }
};

const updateExportSetting = async (form: ExportSettingForm) => {
  if (!form.id) {
    console.error('不正な呼び出し');
    return;
  }

  // リスク評価はファイル形式・エクスポート形式・文字コードの選択を持たないため送信しない
  const isRiskAssessment = form.exportType === 'risk_assessment';
  const body: UpdateCompanyPeriodicExportSettingRequest = {
    name: form.name,
    description: form.description,
    export_type: form.exportType,
    export_file_format: isRiskAssessment ? undefined : form.exportFileFormat,
    export_format: isRiskAssessment ? undefined : form.exportFormat,
    export_character_code:
      !isRiskAssessment && form.exportFileFormat !== EXPORT_FILE_FORMAT_EXCEL
        ? form.exportCharacterCode
        : undefined,
    export_time: form.exportTime,
    export_frequency: form.exportFrequency,
    export_date_pattern:
      form.exportFrequency === 'monthly' ? form.exportDatePattern : undefined,
    export_day_of_week:
      form.exportFrequency === 'weekly' ? form.exportDayOfWeek : undefined,
    cc_user_ids: form.recipientCc,
  };

  try {
    const response: AxiosResponse =
      await dispatchUpdateCompanyPeriodicExportSetting(store, {
        companyId,
        settingId: form.id,
        body,
      });
    if (response?.data) {
      commitAddNotification(store, {
        messageKey: m.createSuccess,
        color: 'success',
      });
      await fetchCompanyPeriodicExportSettings(companyId);
    }
  } catch (e) {
    if (e instanceof AxiosError) {
      const detail = e.response?.data.detail;
      commitAddNotification(store, {
        messageKey: sharedMessages.responseError,
        messageValues: {
          detail: detail ? detail : e.response?.data ?? e.message,
        },
        color: 'error',
      });
    } else {
      console.error(e);
      commitAddNotification(store, {
        messageKey: sharedMessages.genericError,
        color: 'error',
      });
    }
  }
};

const deleleExportSetting = async (
  exportSetting: GetCompanyPeriodicExportSetting | undefined
) => {
  if (exportSetting) {
    try {
      await dispatchDeleteCompanyPeriodicExportSetting(store, {
        companyId,
        settingId: exportSetting.id,
      });
      commitAddNotification(store, {
        messageKey: sharedMessages.subjectDeleted,
        messageValues: {
          subject: exportSetting.name,
        },
        color: 'success',
      });
      await fetchCompanyPeriodicExportSettings(companyId);
    } catch (e) {
      if (e instanceof AxiosError) {
        const detail = e.response?.data.detail;
        commitAddNotification(store, {
          messageKey: sharedMessages.responseError,
          messageValues: {
            detail: detail ? detail : e.response?.data ?? e.message,
          },
          color: 'error',
        });
      } else {
        console.error(e);
        commitAddNotification(store, {
          messageKey: sharedMessages.genericError,
          color: 'error',
        });
      }
    }
  } else {
    console.error('削除対象データが存在しない');
  }
};

const fetchCompanyPeriodicExportSettings = async (companyId: string) => {
  const response: AxiosResponse<GetCompanyPeriodicExportSetting[]> =
    await dispatchGetCompanyPeriodicExportSettings(store, {
      companyId,
    });
  if (response?.data) {
    exportSettings.value = response.data;
  }
};

const fetchSendableCompanyUsers = async (companyId: string) => {
  const response: AxiosResponse<GetSendableCompanyUser[]> =
    await dispatchGetSendableCompanyUsers(store, {
      companyId,
    });
  if (response?.data) {
    sendableUsers.value = response.data;
  }
};

onMounted(async () => {
  if (role !== RoleId.USER_ADMIN.value) {
    return;
  }
  // ダイアログ表示用の情報を取得
  await Promise.all([
    fetchCompanyPeriodicExportSettings(companyId),
    fetchCompanyExportOperations(companyId),
    fetchSendableCompanyUsers(companyId),
  ]).catch((e) => {
    hasInitializedError.value = true;
    if (e instanceof AxiosError) {
      const detail = e.response?.data.detail;
      commitAddNotification(store, {
        messageKey: sharedMessages.responseError,
        messageValues: {
          detail: detail ? detail : e.response?.data ?? e.message,
        },
        color: 'error',
      });
    } else {
      console.error(e);
      commitAddNotification(store, {
        messageKey: sharedMessages.genericError,
        color: 'error',
      });
    }
  });
});

return { __sfc: true,MAX_SETTINGS, m, exportOperations, fetchCompanyExportOperations, isOpenCreateDialog, hasInitializedError, exportSettings, sendableUsers, role, companyId, showCreateDialog, closeCreateDialog, isLimitSettings, createExportSetting, updateExportSetting, deleleExportSetting, fetchCompanyPeriodicExportSettings, fetchSendableCompanyUsers, RoleId, PeriodicExportFormDialog, PeriodicExportTable }
}

})